import { useState } from "react";
import { KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, setToken } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function PwField({ id, label, value, onCh, showIt, toggle, testId, autoFocus = false }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={showIt ? "text" : "password"}
          required
          minLength={8}
          value={value}
          onChange={onCh}
          autoFocus={autoFocus}
          autoComplete="new-password"
          className="pr-10"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={toggle}
          aria-label={showIt ? "Hide password" : "Show password"}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          {showIt ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

export default function AdminChangePassword() {
  const nav = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ curr: false, next: false, conf: false });
  const [saving, setSaving] = useState(false);

  // Simple client-side strength meter
  const pwd = form.new_password;
  const strength = (() => {
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
    if (/\d/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    if (pwd.length >= 12) s++;
    return s;   // 0..5
  })();
  const strengthLabel = ["Very weak", "Weak", "Fair", "Good", "Strong", "Excellent"][strength];
  const strengthColor = ["bg-rose-500", "bg-rose-500", "bg-orange-500", "bg-yellow-500", "bg-emerald-500", "bg-emerald-600"][strength];

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation do not match");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    if (form.current_password === form.new_password) {
      toast.error("New password must differ from current password");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Please sign in again with the new password.");
      // Force re-login so the old JWT can't be reused indefinitely
      setToken(null);
      setTimeout(() => nav("/admin/login", { replace: true }), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="admin-change-password">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Change Password</h1>
        <p className="mt-1 text-sm text-slate-500">Rotate your admin password without touching the server. You&apos;ll be signed out after saving.</p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="change-password-form">
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-slate-700">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>Use at least 8 characters — a mix of upper &amp; lower case, digits, and a symbol makes for a stronger password.</div>
        </div>

        <div className="space-y-4">
          <PwField
            id="cp-current" label="Current password" value={form.current_password}
            onCh={onChange("current_password")}
            showIt={show.curr} toggle={() => setShow({ ...show, curr: !show.curr })}
            testId="cp-current" autoFocus
          />
          <PwField
            id="cp-new" label="New password" value={form.new_password}
            onCh={onChange("new_password")}
            showIt={show.next} toggle={() => setShow({ ...show, next: !show.next })}
            testId="cp-new"
          />

          {pwd.length > 0 && (
            <div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Strength</span>
                <span className="font-semibold text-slate-700">{strengthLabel}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full rounded-full transition-all ${strengthColor}`} style={{ width: `${(strength / 5) * 100}%` }} />
              </div>
            </div>
          )}

          <PwField
            id="cp-confirm" label="Confirm new password" value={form.confirm_password}
            onCh={onChange("confirm_password")}
            showIt={show.conf} toggle={() => setShow({ ...show, conf: !show.conf })}
            testId="cp-confirm"
          />
          {form.confirm_password && form.new_password !== form.confirm_password && (
            <p className="text-xs text-rose-600">Passwords do not match</p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2 border-t pt-4">
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="cp-submit">
            <KeyRound className="mr-1 h-4 w-4" />{saving ? "Updating…" : "Update password"}
          </Button>
        </div>
      </form>
    </div>
  );
}
